/**
 * Currency Manager
 * 
 * Manages dual currency system (free regenerating + premium purchasable)
 * Handles currency regeneration, spending, and usage caps
 */

export class CurrencyManager {
    constructor(gameState, apiClient) {
        this.gameState = gameState;
        this.apiClient = apiClient;
        
        // Currency state
        this.freeCurrency = 0;
        this.freeCurrencyMax = 100;
        this.freeCurrencyRegenRate = 1; // 1 per hour
        this.freeCurrencyLastUpdate = Date.now();
        
        this.premiumCurrency = 0;
        
        // Usage tracking
        this.usageCaps = {
            speed_up: { daily: 5, weekly: 20, monthly: 80 },
            instant_complete: { daily: 2, weekly: 8, monthly: 30 },
            resource_boost: { daily: 1, weekly: 5, monthly: 20 }
        };
        
        this.updateInterval = null;
    }
    
    /**
     * Initialize currency from server data
     */
    async initialize() {
        try {
            const data = await this.apiClient.getCurrencies();
            if (data) {
                this.freeCurrency = data.free_currency || 0;
                this.freeCurrencyMax = data.free_currency_max || 100;
                this.premiumCurrency = data.premium_currency || 0;
                
                // Calculate regenerated currency since last update
                if (data.free_currency_last_update) {
                    const lastUpdate = new Date(data.free_currency_last_update).getTime();
                    const now = Date.now();
                    const hoursPassed = (now - lastUpdate) / (1000 * 60 * 60);
                    const regenerated = Math.floor(hoursPassed * this.freeCurrencyRegenRate);
                    
                    this.freeCurrency = Math.min(
                        this.freeCurrency + regenerated,
                        this.freeCurrencyMax
                    );
                    
                    this.freeCurrencyLastUpdate = now;
                } else {
                    this.freeCurrencyLastUpdate = Date.now();
                }
                
                // Start regeneration timer
                this.startRegeneration();
                return true;
            }
        } catch (error) {
            // Server not available - use defaults for offline mode
            console.log('Currency server unavailable, using offline mode:', error.message);
            // Initialize with default values
            this.freeCurrency = 0;
            this.freeCurrencyMax = 100;
            this.premiumCurrency = 0;
            this.freeCurrencyLastUpdate = Date.now();
            
            // Start regeneration timer (will work offline)
            this.startRegeneration();
            return false; // Indicate server connection failed
        }
    }
    
    /**
     * Start currency regeneration timer
     */
    startRegeneration() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
        }
        
        // Update every minute to check for regeneration
        this.updateInterval = setInterval(() => {
            this.updateRegeneration();
        }, 60000); // 1 minute
    }
    
    /**
     * Stop regeneration timer
     */
    stopRegeneration() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
            this.updateInterval = null;
        }
    }
    
    /**
     * Update free currency regeneration
     */
    updateRegeneration() {
        const now = Date.now();
        const hoursPassed = (now - this.freeCurrencyLastUpdate) / (1000 * 60 * 60);
        
        if (hoursPassed >= 1) {
            const regenerated = Math.floor(hoursPassed * this.freeCurrencyRegenRate);
            
            if (regenerated > 0) {
                this.freeCurrency = Math.min(
                    this.freeCurrency + regenerated,
                    this.freeCurrencyMax
                );
                
                this.freeCurrencyLastUpdate = now;
                
                // Sync with server
                this.syncWithServer();
                
                // Emit event
                if (this.gameState && this.gameState.emit) {
                    this.gameState.emit('currencyUpdated', {
                        freeCurrency: this.freeCurrency,
                        premiumCurrency: this.premiumCurrency
                    });
                }
            }
        }
    }
    
    /**
     * Get current currency amounts
     */
    getCurrencies() {
        return {
            free: this.freeCurrency,
            freeMax: this.freeCurrencyMax,
            premium: this.premiumCurrency,
            nextRegenTime: this.getNextRegenerationTime()
        };
    }
    
    /**
     * Get time until next regeneration
     */
    getNextRegenerationTime() {
        if (this.freeCurrency >= this.freeCurrencyMax) {
            return null; // Already at max
        }
        
        const now = Date.now();
        const hoursSinceUpdate = (now - this.freeCurrencyLastUpdate) / (1000 * 60 * 60);
        const hoursUntilNext = 1 - (hoursSinceUpdate % 1);
        
        return Math.ceil(hoursUntilNext * 60); // Minutes until next
    }
    
    /**
     * Check if player can afford a cost
     */
    canAfford(cost, currencyType = 'free') {
        if (currencyType === 'free') {
            return this.freeCurrency >= cost;
        } else if (currencyType === 'premium') {
            return this.premiumCurrency >= cost;
        }
        return false;
    }
    
    /**
     * Check usage cap for a feature
     */
    async checkUsageCap(featureType, periodType = 'daily') {
        try {
            const usage = await this.apiClient.getCurrencyUsage(featureType, periodType);
            const cap = this.usageCaps[featureType]?.[periodType] || 0;
            
            return {
                used: usage || 0,
                cap: cap,
                remaining: Math.max(0, cap - (usage || 0)),
                canUse: (usage || 0) < cap
            };
        } catch (error) {
            console.error('Failed to check usage cap:', error);
            return { used: 0, cap: 0, remaining: 0, canUse: false };
        }
    }
    
    /**
     * Spend currency
     */
    async spendCurrency(amount, currencyType, featureType, description = '') {
        // Check if can afford
        if (!this.canAfford(amount, currencyType)) {
            throw new Error(`Insufficient ${currencyType} currency`);
        }
        
        // Check usage cap
        const capCheck = await this.checkUsageCap(featureType, 'daily');
        if (!capCheck.canUse) {
            throw new Error(`Daily usage cap reached for ${featureType}`);
        }
        
        try {
            // Deduct currency
            if (currencyType === 'free') {
                this.freeCurrency = Math.max(0, this.freeCurrency - amount);
            } else if (currencyType === 'premium') {
                this.premiumCurrency = Math.max(0, this.premiumCurrency - amount);
            }
            
            // Record usage on server
            await this.apiClient.recordCurrencyUsage({
                currencyType,
                featureType,
                amount,
                description
            });
            
            // Sync with server
            await this.syncWithServer();
            
            // Emit event
            if (this.gameState && this.gameState.emit) {
                this.gameState.emit('currencySpent', {
                    amount,
                    currencyType,
                    featureType,
                    remaining: currencyType === 'free' ? this.freeCurrency : this.premiumCurrency
                });
            }
            
            return true;
        } catch (error) {
            // Revert on error
            if (currencyType === 'free') {
                this.freeCurrency += amount;
            } else if (currencyType === 'premium') {
                this.premiumCurrency += amount;
            }
            throw error;
        }
    }
    
    /**
     * Speed up construction
     */
    async speedUpConstruction(queueItemId, speedUpPercent = 50) {
        const cost = {
            free: 5,
            premium: 10
        };
        
        // Try free currency first
        if (this.canAfford(cost.free, 'free')) {
            const capCheck = await this.checkUsageCap('speed_up', 'daily');
            if (capCheck.canUse) {
                await this.spendCurrency(cost.free, 'free', 'speed_up', `Speed up construction ${queueItemId}`);
                return { success: true, currencyType: 'free', cost: cost.free };
            }
        }
        
        // Try premium currency
        if (this.canAfford(cost.premium, 'premium')) {
            const capCheck = await this.checkUsageCap('speed_up', 'daily');
            if (capCheck.canUse) {
                await this.spendCurrency(cost.premium, 'premium', 'speed_up', `Speed up construction ${queueItemId}`);
                return { success: true, currencyType: 'premium', cost: cost.premium };
            }
        }
        
        throw new Error('Cannot afford speed-up or daily cap reached');
    }
    
    /**
     * Instant complete construction
     */
    async instantCompleteConstruction(queueItemId) {
        const cost = {
            free: 20,
            premium: 50
        };
        
        // Try free currency first
        if (this.canAfford(cost.free, 'free')) {
            const capCheck = await this.checkUsageCap('instant_complete', 'daily');
            if (capCheck.canUse) {
                await this.spendCurrency(cost.free, 'free', 'instant_complete', `Instant complete construction ${queueItemId}`);
                return { success: true, currencyType: 'free', cost: cost.free };
            }
        }
        
        // Try premium currency
        if (this.canAfford(cost.premium, 'premium')) {
            const capCheck = await this.checkUsageCap('instant_complete', 'daily');
            if (capCheck.canUse) {
                await this.spendCurrency(cost.premium, 'premium', 'instant_complete', `Instant complete construction ${queueItemId}`);
                return { success: true, currencyType: 'premium', cost: cost.premium };
            }
        }
        
        throw new Error('Cannot afford instant complete or daily cap reached');
    }
    
    /**
     * Sync currency with server
     */
    async syncWithServer() {
        try {
            await this.apiClient.updateCurrencies({
                free_currency: this.freeCurrency,
                free_currency_last_update: new Date(this.freeCurrencyLastUpdate).toISOString()
            });
        } catch (error) {
            console.error('Failed to sync currencies:', error);
        }
    }
    
    /**
     * Add premium currency (from purchase)
     */
    async addPremiumCurrency(amount) {
        this.premiumCurrency += amount;
        await this.syncWithServer();
        
        if (this.gameState && this.gameState.emit) {
            this.gameState.emit('currencyUpdated', {
                freeCurrency: this.freeCurrency,
                premiumCurrency: this.premiumCurrency
            });
        }
    }
    
    /**
     * Cleanup
     */
    destroy() {
        this.stopRegeneration();
    }
}

